import { FC } from "react";
import {
  Drawer,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Button,
  Box,
  makeStyles,
  DialogTitle,
} from "@material-ui/core";
import { useDialogStyles } from "components/modals/styles";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { DaNotificationData } from "types/DaNotifications";
import moment from "moment";
import { useNav } from "hooks/useNav";
import { useDispatch } from "react-redux";
import { updateNotifications } from "actions/notificationsActions";

moment.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s",
    s: "1 s",
    ss: "%d h",
    m: "1 min",
    mm: "%d min",
    h: "1 h",
    hh: "%d h",
    d: "1 d",
    dd: "%d d",
    M: "1 m",
    MM: "%d m",
    y: "1 y",
    yy: "%d y",
  },
});

interface NotificationsDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface NotificationRowProps {
  data: DaNotificationData;
  onClose: () => void;
}

export const NotificationRow: FC<NotificationRowProps> = ({ data, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { viewDaFeedbackReport, viewDaFeedback } = useNav();

  const updateOrRedirectNotify = () => {
    const redirect = () => {
      returnRedirect(data.tag, data.post_id, viewDaFeedbackReport, viewDaFeedback);
      onClose();
    };
    if (!data.is_read) {
      dispatch(updateNotifications(data.id, redirect()));
    } else {
      return redirect();
    }
  };

  return (
    <Box className={classes.notificationBox} onClick={() => updateOrRedirectNotify()}>
      <Box style={{ width: "90%" }} className={`${data.is_read && classes.readText}`}>
        <Typography style={{ fontSize: 18 }} component="div">
          <Box display="inline" className={classes.boldText}>
            {data.title}:
          </Box>{" "}
          {data.message}
        </Typography>
      </Box>
      <Box
        style={{
          width: "10%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Typography style={{ textWrap: "nowrap" }}>
          {moment(data.created).local().fromNow()}
        </Typography>
        {!data.is_read && <Box className={classes.unreadHighlight}></Box>}
      </Box>
    </Box>
  );
};

export const DaNotificationsDrawer: FC<NotificationsDrawerProps> = ({ open, onClose }) => {
  const classes = useStyles();
  const dialogClasses = useDialogStyles();
  const { notificationsList } = useTypeSelectors((state) => state.daNotificationsState);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{ style: { maxWidth: 400 } }}
    >
      <DialogTitle>
        <Typography variant="h4">Notifications:</Typography>
      </DialogTitle>
      <DialogContent style={{ padding: 10 }}>
        <DialogContentText className={classes.contentText}>
          {notificationsList.length ? (
            notificationsList?.map((data: DaNotificationData) => (
              <NotificationRow data={data} onClose={onClose} />
            ))
          ) : (
            <Box className={classes.noMessagesBox}>
              <Typography style={{ fontWeight: 600, color: "#cfd0d1", fontSize: 24 }}>
                You have no messages
              </Typography>
            </Box>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions style={{ padding: "32px 24px", gap: "20px" }}>
        <Button
          color="primary"
          variant="outlined"
          className={dialogClasses.actionButton}
          onClick={onClose}
        >
          Back
        </Button>
      </DialogActions>
    </Drawer>
  );
};

const returnRedirect = (
  tag: string,
  postID: string,
  viewSchedulesFeedbackReport: any,
  viewDaFeedback: any,
) => {
  switch (tag) {
    case "daily_schedule_feedback":
    case "daily_schedule_status":
    case "weekly_schedule_feedback":
    case "weekly_schedule_status":
      return viewSchedulesFeedbackReport(Number(postID));
    case "prime_schedule_feedback":
      return viewDaFeedback();
    default:
      break;
  }
};

const useStyles = makeStyles((theme) => ({
  contentText: {
    minWidth: 300,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "scroll",
    margin: 0,
  },
  noMessagesBox: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  readText: {
    color: "#bbbcbd",
  },
  boldText: {
    fontWeight: 600,
  },
  unreadHighlight: {
    minWidth: 10,
    minHeight: 10,
    borderRadius: 50,
    background: "red",
  },
  notificationBox: {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 16,
    borderRadius: 16,
    padding: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#f3f5f7",
    },
  },
}));
